import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { getDisplayName } from '../../../../models/NameUtils';
import { Button } from '../../../components-v2/Button';
import { Dialog, DialogPopup } from '../../../components-v2/Dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components-v2/Popover';
import { TabToggle } from '../../../components-v2/TabToggle';
import { THEME } from '../../../components-v2/Theme/theme';
import { EmailIcon } from '../../../components/svgs/icons/EmailIcon';
import { getActionItemParagraphs, getActionItemTitle } from '../utils/actionItemText';
import { IDueDateValue } from '../utils/buckets';
import { AssigneeMenu } from './AssigneeMenu';
import { ContactChips, getReferencedContacts } from './ContactChips';
import { DueChip } from './DueChip';
import { FocusContactPanel } from './FocusContactPanel';
import { PriorityFlag } from './PriorityFlag';
import { SnoozeMenu } from './SnoozeMenu';

export interface IFocusModeProps {
	open: boolean;
	onClose: () => void;
	queueIds: string[];
	itemsById: Map<string, Api.IActionItem>;
	onComplete: (item: Api.IActionItem) => void;
	onReschedule: (item: Api.IActionItem, value: IDueDateValue) => void;
	onSetPriority: (item: Api.IActionItem, priority: Api.ActionItemPriority) => void;
	onReassign: (item: Api.IActionItem, user: Api.IUser) => void;
	onSendEmail: (item: Api.IActionItem) => void;
}

function isTypingTarget(target: EventTarget | null): boolean {
	return (
		target instanceof HTMLElement &&
		(target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
	);
}

export const FocusMode: React.FC<IFocusModeProps> = ({
	open,
	onClose,
	queueIds,
	itemsById,
	onComplete,
	onReschedule,
	onSetPriority,
	onReassign,
	onSendEmail,
}) => {
	const [index, setIndex] = React.useState(0);
	const [snoozeOpen, setSnoozeOpen] = React.useState(false);
	const [order, setOrder] = React.useState<'oldest' | 'newest'>('oldest');
	const remaining = React.useMemo(() => {
		const items = queueIds.map(id => itemsById.get(id)).filter((item): item is Api.IActionItem => !!item);
		return order === 'newest' ? items.reverse() : items;
	}, [queueIds, itemsById, order]);
	const total = queueIds.length;
	const completedCount = total - remaining.length;
	const current = remaining[index];
	const isDone = remaining.length === 0 || index >= remaining.length;

	React.useEffect(() => {
		setIndex(0);
		setSnoozeOpen(false);
	}, [queueIds]);

	const next = React.useCallback(() => setIndex(i => Math.min(i + 1, remaining.length)), [remaining.length]);
	const previous = React.useCallback(() => setIndex(i => Math.max(i - 1, 0)), []);
	const completeCurrent = React.useCallback(() => {
		if (current) {
			onComplete(current);
		}
	}, [current, onComplete]);

	React.useEffect(() => {
		if (!open) {
			return;
		}
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.metaKey || event.ctrlKey || event.altKey || isTypingTarget(event.target)) {
				return;
			}
			switch (event.key) {
				case 'c':
					event.preventDefault();
					completeCurrent();
					break;
				case 's':
					event.preventDefault();
					if (current) {
						setSnoozeOpen(value => !value);
					}
					break;
				case 'e':
					event.preventDefault();
					if (current) {
						onSendEmail(current);
					}
					break;
				case 'ArrowRight':
					event.preventDefault();
					next();
					break;
				case 'ArrowLeft':
					event.preventDefault();
					previous();
					break;
				default:
					break;
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [open, current, completeCurrent, next, previous, onSendEmail]);

	const contacts = current ? getReferencedContacts(current) : [];
	const primaryContact = contacts[0] ?? null;

	return (
		<Dialog open={open} onOpenChange={value => !value && onClose()}>
			<DialogPopup styles={[styleSheet.popup]} aria-label='Focus mode'>
				<header className={css(styleSheet.header)}>
					<div className={css(styleSheet.headerText)}>
						<span className={css(styleSheet.eyebrow)}>Focus</span>
						<span className={css(styleSheet.progress)} data-testid='focus-progress'>
							{isDone ? `${completedCount} of ${total} done` : `${completedCount + index + 1} of ${total}`}
						</span>
						<TabToggle
							size='small'
							aria-label='Queue order'
							styles={[styleSheet.orderToggle]}
							value={order}
							onValueChange={value => {
								setOrder(value as 'oldest' | 'newest');
								setIndex(0);
							}}
							items={[
								{ value: 'oldest', label: 'Oldest first' },
								{ value: 'newest', label: 'Newest first' },
							]}
						/>
					</div>
					<div className={css(styleSheet.progressBar)} aria-hidden>
						<div
							className={css(styleSheet.progressFill)}
							style={{
								width: total ? `${Math.round(((completedCount + (isDone ? 0 : index)) / total) * 100)}%` : '0%',
							}}
						/>
					</div>
				</header>
				{isDone || !current ? (
					<div className={css(styleSheet.done)} data-testid='focus-done'>
						<span className={css(styleSheet.doneTitle)}>
							{remaining.length === 0 ? 'Nice work, the queue is clear.' : 'You reached the end of the queue.'}
						</span>
						<span className={css(styleSheet.muted)}>
							{remaining.length === 0
								? `You worked through ${total} action item${total === 1 ? '' : 's'}.`
								: `${remaining.length} skipped item${remaining.length === 1 ? '' : 's'} still open.`}
						</span>
						<div className={css(styleSheet.doneActions)}>
							{remaining.length > 0 ? (
								<Button variant='secondary' onClick={() => setIndex(0)}>
									Review skipped
								</Button>
							) : null}
							<Button onClick={onClose}>Close</Button>
						</div>
					</div>
				) : (
					<>
						<div className={css(styleSheet.body)}>
							<section className={css(styleSheet.itemPanel)} data-testid='focus-item'>
								<h2 className={css(styleSheet.title)}>{getActionItemTitle(current)}</h2>
								{getActionItemParagraphs(current)
									.slice(1)
									.map((paragraph, i) => (
										<p key={i} className={css(styleSheet.paragraph)}>
											{paragraph}
										</p>
									))}
								<div className={css(styleSheet.meta)}>
									<DueChip item={current} size='default' onChange={value => onReschedule(current, value)} />
									<PriorityFlag
										showLabel
										priority={current.priority}
										onChange={priority => onSetPriority(current, priority)}
									/>
									<AssigneeMenu showName assignee={current.assignee} onChange={user => onReassign(current, user)} />
								</div>
								<ContactChips item={current} max={5} />
								{current.creator ? (
									<span className={css(styleSheet.muted)}>Created by {getDisplayName(current.creator)}</span>
								) : null}
							</section>
							<FocusContactPanel contact={primaryContact} />
						</div>
						<footer className={css(styleSheet.footer)}>
							<div className={css(styleSheet.footerGroup)}>
								<Button variant='ghost' size='sm' disabled={index === 0} onClick={previous} title='Previous (←)'>
									Previous
								</Button>
								<Button variant='ghost' size='sm' onClick={next} title='Skip (→)'>
									Skip
								</Button>
							</div>
							<div className={css(styleSheet.footerGroup)}>
								{contacts.length > 0 ? (
									<Button variant='secondary' size='sm' onClick={() => onSendEmail(current)} title='Send email (e)'>
										<EmailIcon fillColor='currentColor' width={14} height={12} />
										<span>Send Email</span>
									</Button>
								) : null}
								<Popover open={snoozeOpen} onOpenChange={setSnoozeOpen}>
									<PopoverTrigger asChild>
										<Button variant='secondary' size='sm' title='Snooze (s)'>
											Snooze
										</Button>
									</PopoverTrigger>
									<PopoverContent align='end'>
										<SnoozeMenu
											current={{ dueDate: current.dueDate ?? null, hasDueTime: !!current.hasDueTime }}
											onSelect={value => {
												setSnoozeOpen(false);
												onReschedule(current, value);
												next();
											}}
										/>
									</PopoverContent>
								</Popover>
								<Button variant='primary' size='sm' onClick={completeCurrent} title='Complete and next (c)'>
									Complete and next
								</Button>
							</div>
						</footer>
						<span className={css(styleSheet.hint)}>
							Shortcuts: <kbd className={css(styleSheet.kbd)}>c</kbd> complete,{' '}
							<kbd className={css(styleSheet.kbd)}>s</kbd> snooze, <kbd className={css(styleSheet.kbd)}>e</kbd> email,{' '}
							<kbd className={css(styleSheet.kbd)}>←</kbd> <kbd className={css(styleSheet.kbd)}>→</kbd> move,{' '}
							<kbd className={css(styleSheet.kbd)}>esc</kbd> exit
						</span>
					</>
				)}
			</DialogPopup>
		</Dialog>
	);
};

const styleSheet = StyleSheet.create({
	popup: {
		width: 'min(1100px, 94vw)',
		maxWidth: 'none',
		height: 'min(720px, 88vh)',
		gap: THEME.spacing.md,
	},
	header: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		paddingRight: THEME.spacing.xxl,
	},
	headerText: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	orderToggle: {
		marginLeft: 'auto',
	},
	eyebrow: {
		fontSize: THEME.typography.fontSizes.xs,
		textTransform: 'uppercase',
		letterSpacing: 0.6,
		color: THEME.colors.text.light,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	progress: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
	},
	progressBar: {
		height: 4,
		borderRadius: 2,
		backgroundColor: THEME.colors.background.main,
		overflow: 'hidden',
	},
	progressFill: {
		height: '100%',
		backgroundColor: THEME.colors.primary.main,
		transition: 'width 300ms ease-out',
	},
	body: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)',
		gap: THEME.spacing.lg,
		flex: 1,
		minHeight: 0,
		[THEME.queries.md]: {
			gridTemplateColumns: 'minmax(0, 1fr)',
		},
	},
	itemPanel: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		minWidth: 0,
		overflowY: 'auto',
	},
	title: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.xl,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		lineHeight: 1.25,
	},
	paragraph: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		whiteSpace: 'pre-wrap',
	},
	meta: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		flexWrap: 'wrap',
	},
	muted: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	footer: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		flexWrap: 'wrap',
		borderTop: `1px solid ${THEME.colors.border.light}`,
		paddingTop: THEME.spacing.md,
	},
	footerGroup: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	hint: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
		textAlign: 'center',
	},
	kbd: {
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadiusSm,
		padding: '0 4px',
		fontFamily: 'inherit',
	},
	done: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: THEME.spacing.sm,
		flex: 1,
		textAlign: 'center',
	},
	doneTitle: {
		fontSize: THEME.typography.fontSizes.xl,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	doneActions: {
		display: 'flex',
		gap: THEME.spacing.sm,
		marginTop: THEME.spacing.md,
	},
});
